/**
 * Cổng chặn sổ tay: chưa đọc xong + xác nhận + nộp bài kiểm tra thì chưa được đi đâu khác.
 *
 * Bọc quanh <Outlet/> của khung ứng dụng. Trước đây popup nhắc là "mềm" — HR không muốn khoá
 * việc của ai; giờ HR đổi ý: sổ tay và bài kiểm tra là bước bắt buộc trước khi dùng system.
 * Chỉ chặn ở giao diện — API không gác; đây là rào cho người, không phải cho kẻ cố tình.
 *
 * Nguồn sự thật là `ack-state` (rẻ, gọi mỗi lần đổi trang): còn nợ xác nhận, hoặc có đề dành
 * cho người này mà chưa nộp → chặn. Không có `quiz` (HR chưa tạo, hoặc đề không cho phòng ban
 * này) thì chỉ đòi xác nhận — không thể bắt ai làm bài họ không mở được.
 */
import { useEffect, type ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { isPageBlocked, type HandbookAckStateDto } from '@system/shared'
import { useT } from '../i18n/language-provider'
import { useAccess } from '../lib/access-context'
import { getHandbookAckState } from '../lib/api-client-employee-handbook'

const DONE_STATUSES = ['submitted', 'partially_graded', 'graded']

/**
 * Trang được mở khi đang bị chặn: sổ tay, chính bài kiểm tra, và Quiz chơi chung. Quiz đi qua
 * cổng vì HR host ván cho cả công ty ngay trong lúc nhiều người còn chưa xong sổ tay — chặn
 * là ván không có ai vào (18/08).
 */
// /team-vote: a roster member votes even before finishing the handbook (user decision 30/09/2026).
export const HANDBOOK_GATE_ALLOWED_PATHS = ['/handbook', '/handbook/start', '/exam/take', '/exam/result', '/exam/my', '/quiz', '/quiz/host', '/team-vote']

export function handbookGateBlocks(state: HandbookAckStateDto | null | undefined): boolean {
  if (!state) return false
  if (state.mustAck) return true
  const quiz = state.quiz
  return !!quiz && !(quiz.attemptStatus !== null && DONE_STATUSES.includes(quiz.attemptStatus))
}

export function EmployeeHandbookGate({ children }: { children: ReactNode }) {
  const t = useT()
  const access = useAccess()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { data } = useQuery({
    // Cùng khoá với popup nhắc, để một lần xác nhận/nộp bài làm mới cả hai.
    queryKey: ['handbook', 'ack-state'],
    queryFn: getHandbookAckState,
    // Chỉ nhân viên nội bộ; đối tác không đọc được sổ tay, gọi là 403.
    enabled: access.isEmployee,
    staleTime: 60 * 1000,
    retry: false,
  })
  // A per-page block on /handbook (user decision 29/09/2026) switches the gate off for that person:
  // sending them to a page the route guard refuses would bounce between the two forever.
  const blocked = access.isEmployee && !isPageBlocked('/handbook', access) && handbookGateBlocks(data?.success ? data.data : null)
  const allowedHere = HANDBOOK_GATE_ALLOWED_PATHS.includes(pathname)

  useEffect(() => {
    // /handbook/start, not /handbook: someone the gate caught has NOT finished reading, and their
    // road is the step walk. The one-page contents view at /handbook is for people who are done.
    if (blocked && !allowedHere) void navigate({ to: '/handbook/start' })
  }, [blocked, allowedHere, navigate])

  if (blocked && !allowedHere) return null
  return (
    <>
      {blocked && (
        <p id="handbook-gate-banner" className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-500">
          {t('handbook.gate.banner')}
        </p>
      )}
      {children}
    </>
  )
}
